import { addMonths, format, formatDistanceToNowStrict } from "date-fns";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronRight,
  Clock3,
  ExternalLink,
  Link2,
  LockKeyhole,
  MoreHorizontal,
  ShieldCheck,
  UserMinus,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { DashboardMember, MemberBucket } from "../shared/types";
import { api } from "./api";
import { BUCKET_LABELS } from "./constants";

export function Badge({ bucket }: { bucket: MemberBucket }) {
  return <span className={`badge badge-${bucket}`}>{BUCKET_LABELS[bucket]}</span>;
}

export function StatusPill({ status }: { status: string }) {
  return <span className={`status-pill status-${status}`}>{status.replaceAll("_", " ")}</span>;
}

export function Avatar({ name, username }: { name: string; username?: string | null }) {
  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const hue = [...(username || name)].reduce((sum, character) => sum + character.charCodeAt(0), 0) % 360;
  return (
    <span className="avatar" style={{ "--avatar-hue": hue } as React.CSSProperties}>
      {initials || "?"}
    </span>
  );
}

export function RelativeTime({ value, empty = "Never observed" }: { value: string | null; empty?: string }) {
  if (!value) return <span className="muted">{empty}</span>;
  const date = new Date(value);
  return (
    <time dateTime={value} title={format(date, "PPpp")}>
      {formatDistanceToNowStrict(date, { addSuffix: true })}
    </time>
  );
}

export function EmptyState({ icon, title, copy }: { icon?: ReactNode; title: string; copy: string }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon ?? <Check size={22} />}</div>
      <h3>{title}</h3>
      <p>{copy}</p>
    </div>
  );
}

export function Modal({ title, copy, children, onClose }: { title: string; copy?: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <h2>{title}</h2>
        {copy && <p className="modal-copy">{copy}</p>}
        {children}
      </section>
    </div>
  );
}

interface MemberTableProps {
  items: DashboardMember[];
  selected: Set<number>;
  onSelected: (selected: Set<number>) => void;
  onChanged: () => void;
  compact?: boolean;
  removalsLocked?: boolean;
  showDirectRemove?: boolean;
}

export function MemberTable({
  items,
  selected,
  onSelected,
  onChanged,
  compact,
  removalsLocked = true,
  showDirectRemove = false,
}: MemberTableProps) {
  const [menuId, setMenuId] = useState<number | null>(null);
  const [whitelistTarget, setWhitelistTarget] = useState<DashboardMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<DashboardMember | null>(null);
  const [linkTarget, setLinkTarget] = useState<DashboardMember | null>(null);
  const [note, setNote] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allSelected = items.length > 0 && items.every((item) => selected.has(item.member.id));

  const openAccess = (item: DashboardMember) => {
    setWhitelistTarget(item);
    setNote(item.whitelist?.note ?? "");
    setExpiresOn(item.whitelist?.expiresAt?.slice(0, 10) ?? "");
    setError(null);
    setMenuId(null);
  };

  const expiryIso = expiresOn ? `${expiresOn}T23:59:59.999Z` : null;
  const today = format(new Date(), "yyyy-MM-dd");

  const act = async (action: () => Promise<unknown>, close: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      close();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="table-wrap">
        <table className="member-table">
          <thead>
            <tr>
              <th className="check-cell">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(event) => {
                    const next = new Set(selected);
                    for (const item of items) {
                      if (event.target.checked) next.add(item.member.id);
                      else next.delete(item.member.id);
                    }
                    onSelected(next);
                  }}
                  aria-label="Select all visible members"
                />
              </th>
              <th>Telegram member</th>
              <th>Classification</th>
              {!compact && <th>Whop identity</th>}
              <th>Last activity</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.member.id} className={selected.has(item.member.id) ? "row-selected" : ""}>
                <td className="check-cell">
                  <input
                    type="checkbox"
                    checked={selected.has(item.member.id)}
                    onChange={(event) => {
                      const next = new Set(selected);
                      if (event.target.checked) next.add(item.member.id);
                      else next.delete(item.member.id);
                      onSelected(next);
                    }}
                    aria-label={`Select ${item.displayName}`}
                  />
                </td>
                <td>
                  <div className="identity-cell">
                    <Avatar name={item.displayName} username={item.member.username} />
                    <div>
                      <strong>{item.displayName}</strong>
                      <span>{item.member.username ? `@${item.member.username}` : `ID ${item.member.telegramUserId}`}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <Badge bucket={item.classification.bucket} />
                  <span className="cell-detail line-clamp" title={item.classification.reason}>{item.classification.reason}</span>
                </td>
                {!compact && (
                  <td>
                    {item.whopUser ? (
                      <div className="whop-cell">
                        <strong>{item.whopUser.name || item.whopUser.username || item.whopUser.id}</strong>
                        <span>{item.whopUser.email || item.link?.method}</span>
                      </div>
                    ) : (
                      <button className="text-button" onClick={() => setLinkTarget(item)}><Link2 size={14} /> Link identity</button>
                    )}
                  </td>
                )}
                <td><RelativeTime value={item.member.lastActivityAt} /></td>
                <td className={`actions-cell ${showDirectRemove ? "review-actions-cell" : ""}`}>
                  <div className="row-actions">
                    {showDirectRemove && !item.classification.isPaid && item.classification.bucket !== "exempt_admin" && (
                      <button
                        className="button secondary compact direct-access-button"
                        onClick={() => openAccess(item)}
                        aria-label={`${item.whitelist ? "Edit" : "Grant"} access for ${item.displayName}`}
                      >
                        <CalendarClock size={14} /> {item.whitelist ? "Renew access" : "Grant access"}
                      </button>
                    )}
                    {showDirectRemove && item.classification.canKick && !["paid", "whitelisted", "exempt_admin"].includes(item.classification.bucket) && (
                      <button
                        className="button danger compact direct-remove-button"
                        onClick={() => { setError(null); setRemoveTarget(item); }}
                        aria-label={`Remove ${item.displayName} from Telegram`}
                      >
                        <UserMinus size={14} /> Remove
                      </button>
                    )}
                    <button className="icon-button" onClick={() => setMenuId(menuId === item.member.id ? null : item.member.id)} aria-label={`More actions for ${item.displayName}`}>
                      <MoreHorizontal size={18} />
                    </button>
                  </div>
                  {menuId === item.member.id && (
                    <div className="action-menu">
                      {item.whitelist ? (
                        <>
                          <button onClick={() => openAccess(item)}><CalendarClock size={15} /> Edit access or expiry</button>
                          <button onClick={() => void act(() => api.removeWhitelist(item.member.id), () => setMenuId(null))}><X size={15} /> Remove access grant</button>
                        </>
                      ) : (
                        <button onClick={() => openAccess(item)}><ShieldCheck size={15} /> Grant access</button>
                      )}
                      {item.link ? (
                        <button onClick={() => void act(() => api.unlink(item.member.id), () => setMenuId(null))}><Link2 size={15} /> Unlink Whop</button>
                      ) : (
                        <button onClick={() => { setLinkTarget(item); setMenuId(null); }}><Link2 size={15} /> Link Whop</button>
                      )}
                      {!showDirectRemove && !removalsLocked && item.classification.canKick && !["paid", "whitelisted", "exempt_admin"].includes(item.classification.bucket) && (
                        <button className="danger-menu" onClick={() => { setRemoveTarget(item); setMenuId(null); }}><UserMinus size={15} /> Remove from group</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {whitelistTarget && (
        <Modal title={`${whitelistTarget.whitelist ? "Edit access for" : "Grant access to"} ${whitelistTarget.displayName}`} copy="Use this for crypto payments, complimentary access, or another approved exception outside Whop. Every change is recorded in the audit log." onClose={() => setWhitelistTarget(null)}>
          <div className="access-form">
            <label className="field-label">Reason <textarea autoFocus value={note} onChange={(event) => setNote(event.target.value)} placeholder="e.g. Paid via crypto — 3 months" rows={3} /></label>
            <label className="field-label">Access expiry date <input type="date" min={today} value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} /><small>Leave blank for permanent access. A selected date remains valid through 23:59 UTC.</small></label>
            <div className="expiry-presets" aria-label="Quick expiry choices">
              <span>Quick select</span>
              {[1, 3, 6, 12].map((months) => (
                <button key={months} type="button" onClick={() => setExpiresOn(format(addMonths(new Date(), months), "yyyy-MM-dd"))}>{months} {months === 1 ? "month" : "months"}</button>
              ))}
              {expiresOn && <button type="button" onClick={() => setExpiresOn("")}>Permanent</button>}
            </div>
            {expiresOn && (
              <div className="warning-panel access-expiry-warning"><CalendarClock size={20} /><div><strong>Automatic live removal after expiry</strong><span>After {format(new Date(`${expiresOn}T12:00:00Z`), "PPP")}, the bot will remove this member even if they remain active. An active Whop payment, an extended grant, or an admin role will stop the removal. The master removal lock can pause it.</span></div></div>
            )}
          </div>
          {error && <div className="inline-error"><AlertTriangle size={15} /> {error}</div>}
          <div className="modal-actions"><button className="button secondary" onClick={() => setWhitelistTarget(null)}>Cancel</button><button className="button primary" disabled={busy || !note.trim()} onClick={() => void act(() => api.whitelist(whitelistTarget.member.id, { note: note.trim(), expiresAt: expiryIso }), () => setWhitelistTarget(null))}>{busy ? "Saving…" : "Save access"}</button></div>
        </Modal>
      )}

      {removeTarget && removalsLocked && (
        <Modal title="Unlock manual removals first" copy={`${removeTarget.displayName} has not been removed. The master safety lock currently blocks every live Telegram removal.`} onClose={() => setRemoveTarget(null)}>
          <div className="warning-panel"><LockKeyhole size={20} /><div><strong>Nothing has been sent to Telegram</strong><span>Open Settings and explicitly unlock live removals. Automatic enforcement will remain off, so only members you confirm manually can be removed.</span></div></div>
          <div className="modal-actions"><button className="button secondary" onClick={() => setRemoveTarget(null)}>Cancel</button><Link className="button danger" to="/settings" onClick={() => setRemoveTarget(null)}>Open Settings to unlock</Link></div>
        </Modal>
      )}

      {removeTarget && !removalsLocked && (
        <Modal title={`Remove ${removeTarget.displayName}?`} copy="The bot will re-check payment, whitelist, and Telegram role immediately before acting. It will remove the member but not permanently ban them, so they can rejoin after paying." onClose={() => setRemoveTarget(null)}>
          <div className="warning-panel"><UserMinus size={20} /><div><strong>This is a live Telegram action</strong><span>Dashboard preview mode only applies to automatic enforcement, not this manual decision.</span></div></div>
          {error && <div className="inline-error"><AlertTriangle size={15} /> {error}</div>}
          <div className="modal-actions"><button className="button secondary" onClick={() => setRemoveTarget(null)}>Keep member</button><button className="button danger" disabled={busy} onClick={() => void act(() => api.removeMember(removeTarget.member.id), () => setRemoveTarget(null))}>{busy ? "Queueing…" : "Remove from Telegram"}</button></div>
        </Modal>
      )}

      {linkTarget && <LinkMemberModal member={linkTarget} onClose={() => setLinkTarget(null)} onChanged={onChanged} />}
    </>
  );
}

function LinkMemberModal({ member, onClose, onChanged }: { member: DashboardMember; onClose: () => void; onChanged: () => void }) {
  const [items, setItems] = useState<Array<{ id: string; username: string | null; name: string | null; email: string | null; statuses: string[]; products: string[] }>>([]);
  const [selected, setSelected] = useState("");
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void api.unlinkedWhop().then((result) => setItems(result.items)).catch((caught) => setError(caught.message));
  }, []);
  const filtered = items.filter((item) => [item.name, item.username, item.email, item.id].some((value) => value?.toLowerCase().includes(query.toLowerCase())));
  return (
    <Modal title={`Link Whop identity to ${member.displayName}`} copy="Only use this when you have independently confirmed both identities. Members can self-verify with /link in the bot instead." onClose={onClose}>
      <label className="field-label">Find an unlinked Whop customer<input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, email, username, or ID" /></label>
      <div className="link-results">
        {filtered.slice(0, 8).map((item) => (
          <label key={item.id} className={`link-option ${selected === item.id ? "selected" : ""}`}>
            <input type="radio" name="whop-user" value={item.id} checked={selected === item.id} onChange={() => setSelected(item.id)} />
            <div><strong>{item.name || item.username || item.id}</strong><span>{item.email || item.id}</span><small>{item.products.join(", ") || "No product"} · {item.statuses.join(", ")}</small></div>
            <ChevronRight size={16} />
          </label>
        ))}
        {items.length === 0 && !error && <div className="loading-row">Loading Whop customers…</div>}
        {items.length > 0 && filtered.length === 0 && <div className="loading-row">No matching unlinked customer</div>}
      </div>
      {error && <div className="inline-error"><AlertTriangle size={15} /> {error}</div>}
      <div className="modal-actions"><button className="button secondary" onClick={onClose}>Cancel</button><button className="button primary" disabled={!selected || busy} onClick={() => { setBusy(true); setError(null); void api.link(member.member.id, selected).then(() => { onChanged(); onClose(); }).catch((caught) => setError(caught.message)).finally(() => setBusy(false)); }}>{busy ? "Linking…" : "Confirm identity link"}</button></div>
    </Modal>
  );
}

export function IntegrationIcon({ healthy, configured }: { healthy: boolean; configured: boolean }) {
  if (healthy) return <span className="integration-icon healthy"><Check size={16} /></span>;
  if (!configured) return <span className="integration-icon missing"><Clock3 size={16} /></span>;
  return <span className="integration-icon warning"><AlertTriangle size={16} /></span>;
}

export function ExternalDocLink({ href, children }: { href: string; children: ReactNode }) {
  return <a className="text-button" href={href} target="_blank" rel="noreferrer">{children} <ExternalLink size={13} /></a>;
}
